// Imports
import React from 'react';
import styled, { css } from 'styled-components/macro';
import Tippy from '@tippyjs/react';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LinkProps } from 'react-router-dom';
import LinkButton from '../../../../../../shareables/foundation/front-end/components/buttons/link';
import { StylingProps } from '../../../../../../shareables/foundation/front-end/components/buttons/standard';
import tippyProps from '../../../../../../shareables/foundation/front-end/utils/tippy-props';
import { darken, lighten, transparentize } from 'polished';
import { GeneratedThemeProps } from '../../../../../templates/template-style';


// Extra styling
const extraStyling = css<StylingProps & { theme: GeneratedThemeProps }>`
	span {
		outline: none;
		display: inline-flex;
		align-items: center;
	}
	
	&& {
		text-decoration: none;
	}
	
	${(props) => {
		const { baseColor } = props.theme;
		
		return (
			props.color === 'primary' &&
			css`
				background-color: ${props.theme.isPaper ? lighten(0.4, baseColor) : darken(0.05, baseColor)};
				border: 0.0625rem solid ${props.theme.isPaper ? lighten(0.4, baseColor) : baseColor};
				
				&:hover {
					background-color: ${props.theme.isPaper ? lighten(0.35, baseColor) : darken(0.05, baseColor)} !important;
				}
				
				&:focus {
					box-shadow:
						0 0.125em 0.25em rgba(0, 0, 0, 0.05),
						0 0 0 0.125em #fff,
						0 0 0 0.25em ${transparentize(0.75, darken(0.05, baseColor))} !important;
				}
			`
		);
	}}
`;


// Styled components
const StyledLinkButton = styled(LinkButton)<StylingProps & { theme: GeneratedThemeProps }>`
	${extraStyling}
`;


// Define the accepted props
type LimitedStylingProps = Omit<StylingProps, 'border' | 'useBackgroundColorWithFocus'>;

interface BaseProps extends LimitedStylingProps {
	icon: IconDefinition;
	text: string;
	tooltip?: string;
	storybookTooltipIsVisible?: true;
	'data-intercom-target'?: string;
}

type LinkButtonProps = Omit<LinkProps, 'color'> & BaseProps;


// Function component
const PageAction: React.FC<LinkButtonProps> = ({
	color,
	inverted,
	icon,
	text,
	tooltip,
	storybookTooltipIsVisible,
	...props
}) => {
	// Prevent some problematic usages
	if (icon && icon.prefix !== 'far' && icon.prefix !== 'fab') {
		throw new Error('A page action’s icon should come from the "regular" or "brand" package');
	}
	
	
	// Return JSX
	return (
		<Tippy {...tippyProps} content={tooltip || ''} visible={tooltip ? storybookTooltipIsVisible : false}>
			<StyledLinkButton
				color={color ?? 'plain'}
				inverted={inverted ?? true}
				border
				useBackgroundColorWithFocus
				{...props}
				state={{ disableScrollToTop: true }}
			>
				<FontAwesomeIcon icon={icon} />
				<span>{text}</span>
			</StyledLinkButton>
		</Tippy>
	);
};

export default PageAction;
